const h2Class =
  "text-[30px] text-[#cc4422] font-bold font-['Montserrat',sans-serif] mb-4 mt-10"
const h3Class =
  "text-[20px] text-gray-800 font-bold font-['Montserrat',sans-serif] mb-2 mt-6"
const pClass = 'text-[17px] leading-[1.65] text-justify mb-4 text-gray-600'
const ulClass = 'list-disc list-outside mb-6 space-y-2 pl-8'
const liClass = 'text-[17px] leading-[1.65] text-justify text-gray-600'

export function HtmlEditorInfo() {
  return (
    <section className="max-w-4xl mx-auto px-5 py-5">
      <h2 className={h2Class}>What is HTML editor</h2>
      <p className={pClass}>
        An HTML editor is a tool for creating and editing HTML markup. The
        HTML Editor here is a free WYSIWYG (What You See Is What You Get)
        editor that lets you compose content visually and gives you clean
        HTML output in real time — no installation, no sign-up.
      </p>

      <h3 className={h3Class}>WYSIWYG vs. source code editing</h3>
      <p className={pClass}>
        A plain text editor expects you to write each tag by hand. The HTML
        Editor lets you type, format, and arrange content the way you want
        it to look, and generates the corresponding markup beside the editing
        surface. That makes it easier for non-developers to produce usable
        HTML, and faster for experienced users to draft snippets they can
        refine in code.
      </p>

      <h3 className={h3Class}>Browser-based, no installation</h3>
      <p className={pClass}>
        The HTML Editor runs entirely in your browser. You can convert
        documents from Word, PDF, or other formats into clean HTML, draft
        snippets for a website, email, or newsletter, and copy the output
        into any project when you're done.
      </p>

      <h2 className={h2Class}>How a WYSIWYG HTML Editor works</h2>

      <h3 className={h3Class}>Visual editing surface</h3>
      <p className={pClass}>
        You type and format content in the visual pane and the HTML Editor
        renders it the same way a browser would. Bold, italic, alignment,
        lists, links, anchors, and images are all available from the toolbar;
        undo and redo let you step back through changes without retyping.
      </p>

      <h3 className={h3Class}>Generated source output</h3>
      <p className={pClass}>
        Every change you make in the visual pane is reflected as HTML in the
        source pane. You can copy the markup directly, or edit the source and
        see the visual pane update. When uploading HTML to your web server,
        use a{' '}
        <a
          href="https://chmodcommand.com"
          target="_blank"
          rel="noreferrer noopener"
        >
          chmod calculator to set file permissions
        </a>
        .
      </p>

      <h2 className={h2Class}>What to look for in a HTML Editor Online</h2>
      <p className={pClass}>
        The HTML Editor covers the capabilities that separate a usable online
        editor from a frustrating one.
      </p>

      <h3 className={h3Class}>Clean output</h3>
      <p className={pClass}>
        The HTML Editor produces valid, W3C-compliant markup. Copy and paste
        works without dragging in foreign formatting — though pasting from a
        Word document tends to inject styles that don't belong in production
        HTML, so prefer plain-text sources like Notepad.
      </p>

      <h3 className={h3Class}>Formatting and styling</h3>
      <p className={pClass}>
        Text and background color controls, underline, superscript and
        subscript, strikethrough, and table support with adjustable cell
        spacing, borders, and alignment. Tables in particular are awkward to
        write by hand and a strong reason to reach for the HTML Editor's
        visual pane.
      </p>

      <h3 className={h3Class}>Cross-browser, no install</h3>
      <p className={pClass}>
        No Mac- or Windows-only desktop app to install or update — the HTML
        Editor runs in any modern browser on every platform.
      </p>

      <h2 className={h2Class}>How to use a HTML Editor Online</h2>
      <p className={pClass}>
        Open the HTML Editor in your browser and start a new document. Most
        workflows look like this:
      </p>

      <h3 className={h3Class}>Headings and paragraphs</h3>
      <p className={pClass}>
        Assign Heading 1 to your title, then use Heading 2–6 for subheadings.
        Paragraphs are wrapped automatically — type as you would in a word
        processor and the HTML Editor inserts the opening and closing tags.
      </p>

      <h3 className={h3Class}>Inserting links, images, and tables</h3>
      <p className={pClass}>
        Use the HTML Editor's toolbar to add a link (URL, title, display
        text), an image (source, dimensions, description), embedded media,
        or a table. Images should be hosted somewhere reachable; resize large
        files to keep page weight down.
      </p>

      <h3 className={h3Class}>Formatting and special characters</h3>
      <p className={pClass}>
        Apply bold, italic, alignment, color, indentation, and bullet or
        numbered lists from the toolbar. The Insert menu also handles dates,
        times, and special characters:
      </p>
      <ul className={ulClass}>
        <li className={liClass}>£ pound, ¥ yen, ™ trademark, © copyright</li>
        <li className={liClass}>∫ integral, ∑ summation, √ square root</li>
        <li className={liClass}>Date e.g. 2017-01-20, time e.g. 3:27:50 PM</li>
      </ul>

      <h2 className={h2Class}>Can be used as a Text Editor</h2>

      <h3 className={h3Class}>Plain-text mode</h3>
      <p className={pClass}>
        The HTML Editor doubles as a lightweight text editor. Built-in OS
        tools like Notepad on Windows or TextEdit on macOS are limited;
        popular upgrades include Notepad++, Atom, and Sublime Text. The HTML
        Editor covers the same copy, cut, paste, undo, and redo basics, with
        the added benefit of a synchronized visual pane and HTML source view.
      </p>

      <h2 className={h2Class}>HTML Editor Features</h2>
      <p className={pClass}>
        The HTML Editor is a stable WYSIWYG editor with broad cross-browser
        support and reliable HTML output. The toolbar covers everything you'd
        expect from a modern editor.
      </p>

      <h3 className={h3Class}>Text formatting</h3>
      <ul className={ulClass}>
        <li className={liClass}>Bold and italic for emphasis</li>
        <li className={liClass}>Underline to mark important text</li>
        <li className={liClass}>Strikethrough for deletions or transitions</li>
        <li className={liClass}>Superscript and subscript</li>
        <li className={liClass}>Text color and background color</li>
      </ul>

      <h3 className={h3Class}>Headings and structure</h3>
      <p className={pClass}>
        All six HTML heading levels are available — h1 through h6 — alongside
        left, center, right, and justified alignment, indentation, and bullet
        or numbered lists.
      </p>

      <h3 className={h3Class}>Tables, images, and media</h3>
      <p className={pClass}>
        Insert tables by specifying rows, columns, and cell content. Add
        images directly into the document with an advanced dialog for size,
        alt text, and styles, embed media via the links button, and adjust
        dimensions and alignment from the toolbar. Undo and redo let you
        correct mistakes without losing work.
      </p>

      <h3 className={h3Class}>Productivity tools</h3>
      <p className={pClass}>
        The HTML Editor ships with the workflow features you'd expect from a
        full-featured tool:
      </p>
      <ul className={ulClass}>
        <li className={liClass}>
          Find and replace across the document
        </li>
        <li className={liClass}>
          Live word count for drafting against a target length
        </li>
        <li className={liClass}>
          Preview and fullscreen modes for a distraction-free view
        </li>
        <li className={liClass}>
          Syntax-highlighted code samples for technical content
        </li>
        <li className={liClass}>
          Named anchors for in-page links
        </li>
        <li className={liClass}>
          Emoticons and special-character insertion
        </li>
        <li className={liClass}>
          Auto-linking — pasted URLs become clickable links automatically
        </li>
      </ul>

      <h2 className={h2Class}>HTML Editor Conclusion</h2>
      <p className={pClass}>
        The HTML Editor lets you compose HTML in your browser, for free, with
        no registration or download. The visual pane mirrors what a browser
        will render, and the source pane gives you clean, W3C-compliant markup
        you can paste into any project — websites, emails, blog posts, or
        newsletters.
      </p>
      <p className={pClass}>
        Browser quirks mean no document renders identically everywhere — screen
        sizes, browser bugs, and accessibility tools all interpret the same
        markup slightly differently. Sticking to standard HTML and semantic
        tags keeps your content portable, and the HTML Editor's W3C-compliant
        output gives you a solid baseline to build on.
      </p>
    </section>
  )
}
